import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { LogoBlock } from "../components/LogoBlock/LogoBlock";
import { reportThemes as themes } from "../themes";
import { BackgroundBlocks } from "../components/BackgroundBlocks/BackgroundBlocks";
import { FooterBlock } from "../components/FooterBlock/FooterBlock";
import { Body } from "../components/Body/Body";
import {
  BodyHeader,
  BodyHeaderItem,
} from "../components/BodyHeader/BodyHeader";
import { Table } from "../components/Table/Table";
import generalStyle from "../styles";

const headersTable1 = [
  {
    value: "Date Range",
    width: "23%",
    textAlign: "left",
  },
  {
    value: "Complete",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Sick",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "kcal*",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "MCF*",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "LCF*",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Protein*",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Carb*",
    width: "11%",
    textAlign: "right",
  },
];

const headersTable2 = [
  {
    value: "Date",
    width: "23%",
    textAlign: "left",
  },
  {
    value: "Complete",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Sick",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "kcal",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "MCF",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "LCF",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Protein",
    width: "11%",
    textAlign: "right",
  },
  {
    value: "Carb",
    width: "11%",
    textAlign: "right",
  },
];

const reportStyle = StyleSheet.create({
  note: {
    ...generalStyle.bold,
    fontSize: 8,
    marginTop: 7,
  },
  text: {
    fontSize: 10,
  },
});

const ITEMS_PER_PAGE = 14;

const DailyAveragesReport = (data, { theme = "rarify" }) => {
  const {
    participantID,
    generatedDate,
    appStudy,
    dateRange,
    totalPerDay,
    totalsData,
  } = data;
  const themeData = themes[theme];

  return (
    <Document data-testid="daily-averages-report">
      <Page size="A4" style={generalStyle.page}>
        {Array.apply(
          null,
          Array(Math.ceil(totalPerDay.length / ITEMS_PER_PAGE))
        )
          .map((x, i) => i + 1)
          .map((page) => (
            <View
              break={page !== 1}
              key={`key_${page}`}
              style={{
                flex: 1,
              }}
            >
              <View>
                <BackgroundBlocks {...{ themeData }} />
                <LogoBlock {...{ themeData }} />
              </View>
              <Body {...{ themeData }} title="Daily Averages">
                <BodyHeader {...{ themeData }}>
                  <BodyHeaderItem
                    title="Protocol Number:"
                    subTitle={appStudy.toUpperCase()}
                  />
                  <BodyHeaderItem
                    title="Participant ID:"
                    subTitle={participantID}
                  />
                </BodyHeader>
                <Table headers={headersTable1} {...{ themeData }}>
                  <View>
                    <Text
                      style={{
                        ...generalStyle.semiBold,
                        ...reportStyle.text,
                      }}
                    >
                      {dateRange}
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.completedDays} day(s)
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.sickDays} day(s)
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.calories}
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.mcf}
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.lcf}
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.protein}
                    </Text>
                    <Text
                      style={{
                        ...reportStyle.text,
                        ...generalStyle.semiBold,
                      }}
                    >
                      {totalsData.carbs}
                    </Text>
                  </View>
                </Table>
                <Table headers={headersTable2} {...{ themeData }}>
                  {totalPerDay
                    .slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
                    .map((row, index) => (
                      <View key={index}>
                        <Text
                          style={{
                            ...generalStyle.bold,
                            ...reportStyle.text,
                          }}
                        >
                          {row.date}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                            ...generalStyle.semiBold,
                          }}
                        >
                          {row.completed}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.sickDay}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.calories}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.mcf}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.lcf}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.protein}
                        </Text>
                        <Text
                          style={{
                            ...reportStyle.text,
                          }}
                        >
                          {row.carbs}
                        </Text>
                      </View>
                    ))}
                </Table>
                <Text style={reportStyle.note}>
                  N/A: a day with no data | NC: has data, day not complete |
                  0.0%: complete day without meals
                </Text>
                <Text style={reportStyle.note}>
                  * Note: kcal, MCF, LCF, Protein and Carbs are averages of the
                  complete days in the date range (including fasting days)
                </Text>
              </Body>
            </View>
          ))}
        <FooterBlock fixed {...{ themeData }} {...{ generatedDate }} />
      </Page>
    </Document>
  );
};

export default DailyAveragesReport;
